import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { OCCUPANCY_TIERS, OCC_LABEL, pickOcc, type Occupancy } from "../../utils/occupancy";
import axios from "axios";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:5050";
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DAY_W = 42;
const LABEL_W = 140;

type RoomSummary = {
  roomId: string;
  roomNumber: string | number;
  roomType: string;
};

function localISO(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

function formatRate(rate: number): string {
  if (rate === 0) return "—";
  if (rate >= 10000) return `${Math.round(rate / 1000)}k`;
  if (rate >= 1000) return `${(rate / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return String(Math.round(rate));
}

function rateStyle(rate: number, baseRate: number, source: "manual" | "pricing" | "base"): { color: string; bg: string; weight: number } {
  if (rate === 0 && baseRate === 0)  return { color: "rgba(242,235,217,0.22)", bg: "transparent", weight: 400 };
  if (source === "manual") {
    if (rate < baseRate) return { color: "#34d399", bg: "rgba(52,211,153,0.15)", weight: 700 };
    if (rate > baseRate) return { color: "#fbbf24", bg: "rgba(245,158,11,0.15)", weight: 700 };
    return                      { color: "rgba(242,235,217,0.55)", bg: "rgba(242,235,217,0.04)", weight: 400 };
  }
  if (source === "pricing")  return { color: "#93c5fd", bg: "rgba(147,197,253,0.08)", weight: 500 };
  return { color: "rgba(242,235,217,0.45)", bg: "rgba(242,235,217,0.03)", weight: 400 };
}

// Returns the season double rate for a given roomType + date in a specific market
function seasonRateFor(seasons: any[], marketCode: string | null, roomType: string, dateISO: string, occ: Occupancy = "double"): number | null {
  if (!seasons?.length || !roomType || !marketCode) return null;
  for (const s of seasons) {
    if (dateISO >= s.valid_from && dateISO <= s.valid_to) {
      const r = s.rates?.[roomType]?.[marketCode];
      const v = pickOcc(r, occ);
      if (v > 0) return v;
    }
  }
  return null;
}

function genDays(year: number) {
  const days: { iso: string; month: number; day: number; isWeekend: boolean; isMonthEnd: boolean }[] = [];
  const cur = new Date(year, 0, 1, 12);
  while (cur.getFullYear() === year) {
    const dow = cur.getDay();
    const daysInMonth = new Date(cur.getFullYear(), cur.getMonth() + 1, 0).getDate();
    days.push({
      iso: localISO(cur),
      month: cur.getMonth(),
      day: cur.getDate(),
      isWeekend: dow === 0 || dow === 6,
      isMonthEnd: cur.getDate() === daysInMonth,
    });
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}

const FS: React.CSSProperties = {
  background: "rgba(14,30,56,0.9)",
  border: "1px solid rgba(201,168,76,0.2)",
  borderRadius: 8,
  padding: "8px 12px",
  color: "#F2EBD9",
  fontSize: 13,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

export default function RoomRatesCalendar({
  rooms,
  authHeaders,
}: {
  rooms: RoomSummary[];
  authHeaders: Record<string, string>;
}) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [baseRates, setBaseRates] = useState<Record<string, number>>({});
  const [overrides, setOverrides] = useState<Record<string, number>>({});
  // pricing seasons from /api/hotel/pricing — used as default when no manual override exists
  const [pricingSeasons, setPricingSeasons] = useState<any[]>([]);
  const [selectedOccupancy, setSelectedOccupancy] = useState<Occupancy>("double");
  const [pricingMarkets, setPricingMarkets] = useState<any[]>([]);
  // Which market's prices the calendar displays. Defaults to the FIRST (local) market,
  // NOT the "default" one — the default flag is only for foreign WhatsApp guests.
  const [selectedMarket, setSelectedMarket] = useState<string>("");
  const currency = pricingMarkets.find(m => m.code === selectedMarket)?.currency || "EGP";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const calRef = useRef<HTMLDivElement>(null);

  // Selection (per-room drag)
  const [selRoom, setSelRoom] = useState<string | null>(null);
  const [selStart, setSelStart] = useState("");
  const [selEnd, setSelEnd] = useState("");
  const isDragging = useRef(false);
  const [rateInput, setRateInput] = useState("");

  // Rate range (min/max from hotels table)
  const [rateRange, setRateRange] = useState<{ min: number; max: number } | null>(null);

  // Bulk by type
  const roomTypes = useMemo(() => [...new Set(rooms.map(r => r.roomType).filter(Boolean))].sort(), [rooms]);
  const [bulkType, setBulkType] = useState("");
  const [bulkFrom, setBulkFrom] = useState("");
  const [bulkTo, setBulkTo] = useState("");
  const [bulkRate, setBulkRate] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkToast, setBulkToast] = useState("");

  const bulkTypeRooms = useMemo(
    () => rooms.filter(r => r.roomType === bulkType).length,
    [rooms, bulkType]
  );

  const handleBulkApply = async () => {
    if (!bulkType || !bulkFrom || !bulkTo || bulkRate === "") return;
    setBulkSaving(true);
    setBulkToast("");
    try {
      // Generate dates between bulkFrom and bulkTo inclusive
      const dates: string[] = [];
      const cur = new Date(bulkFrom + "T12:00:00");
      const end = new Date(bulkTo + "T12:00:00");
      while (cur <= end) {
        dates.push(localISO(cur));
        cur.setDate(cur.getDate() + 1);
      }
      if (dates.length === 0) throw new Error("Invalid date range");
      await axios.post(`${API_BASE}/api/roomrates/dates/bulk-type`, {
        roomType: bulkType,
        dates,
        rate: parseFloat(bulkRate),
      }, { headers: authHeaders });

      // Update local override state
      const updated = { ...overrides };
      for (const room of rooms.filter(r => r.roomType === bulkType)) {
        for (const d of dates) updated[`${room.roomId}|${d}`] = parseFloat(bulkRate);
      }
      setOverrides(updated);

      // Refresh rate range
      const rr = await axios.get(`${API_BASE}/api/roomrates/rate-range`, { headers: authHeaders });
      setRateRange(rr.data || null);

      setBulkToast(`Applied ${currency} ${parseFloat(bulkRate).toLocaleString()} to ${bulkTypeRooms} room${bulkTypeRooms !== 1 ? "s" : ""} × ${dates.length} night${dates.length !== 1 ? "s" : ""}`);
      setTimeout(() => setBulkToast(""), 4000);
    } catch (err: any) {
      setBulkToast("Error: " + (err?.response?.data?.error ?? err?.message));
      setTimeout(() => setBulkToast(""), 4000);
    } finally {
      setBulkSaving(false);
    }
  };

  const days = useMemo(() => genDays(year), [year]);

  const monthGroups = useMemo(() => {
    const groups: { month: number; start: number; count: number }[] = [];
    let cur = { month: days[0]?.month ?? 0, start: 0, count: 0 };
    for (let i = 0; i < days.length; i++) {
      if (days[i].month !== cur.month) {
        groups.push({ ...cur });
        cur = { month: days[i].month, start: i, count: 1 };
      } else { cur.count++; }
    }
    if (cur.count > 0) groups.push(cur);
    return groups;
  }, [days]);

  const fetchRates = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [baseRes, datesRes, rangeRes, pricingRes] = await Promise.all([
        axios.get(`${API_BASE}/api/roomrates`, { headers: authHeaders }),
        axios.get(`${API_BASE}/api/roomrates/dates`, {
          params: { from: `${year}-01-01`, to: `${year}-12-31` },
          headers: authHeaders,
        }),
        axios.get(`${API_BASE}/api/roomrates/rate-range`, { headers: authHeaders }).catch(() => ({ data: null })),
        axios.get(`${API_BASE}/api/hotel/pricing`, { headers: authHeaders }).catch(() => ({ data: null })),
      ]);
      const base: Record<string, number> = {};
      for (const r of (baseRes.data || [])) {
        base[String(r.roomid ?? r.RoomID)] = Number(r.baserate ?? r.BaseRate ?? r.base_rate) || 0;
      }
      setBaseRates(base);
      const ov: Record<string, number> = {};
      for (const r of (datesRes.data || [])) {
        ov[`${r.roomid ?? r.RoomID}|${String(r.rate_date).slice(0, 10)}`] = Number(r.rate);
      }
      setOverrides(ov);
      setRateRange(rangeRes.data || null);
      // Load pricing seasons as default rate source
      const p = pricingRes.data?.pricing;
      const mkts = p?.markets || [];
      setPricingSeasons(p?.seasons || []);
      setPricingMarkets(mkts);
      // Default to first market (local currency), not the foreign-guest default
      setSelectedMarket(prev => prev || mkts[0]?.code || "");
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? "Failed to load rates");
    } finally {
      setLoading(false);
    }
  }, [year, authHeaders]);

  useEffect(() => { fetchRates(); }, [fetchRates]);

  // Scroll to today on load
  useEffect(() => {
    if (!loading && calRef.current) {
      const today = new Date();
      if (today.getFullYear() === year) {
        const dayOfYear = Math.floor((today.getTime() - new Date(year, 0, 1).getTime()) / 86400000);
        calRef.current.scrollLeft = Math.max(0, dayOfYear * DAY_W - 300);
      }
    }
  }, [loading, year]);

  useEffect(() => {
    const up = () => { isDragging.current = false; };
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }, []);

  const selMin = selStart && selEnd ? (selStart <= selEnd ? selStart : selEnd) : selStart;
  const selMax = selStart && selEnd ? (selStart >= selEnd ? selStart : selEnd) : selStart;

  const selectedDates = useMemo(() => {
    if (!selRoom || !selMin || !selMax) return [];
    return days.filter(d => d.iso >= selMin && d.iso <= selMax).map(d => d.iso);
  }, [days, selRoom, selMin, selMax]);

  const handleSetRate = async () => {
    if (!selRoom || selectedDates.length === 0 || rateInput === "") return;
    setSaving(true);
    setError("");
    try {
      await axios.post(`${API_BASE}/api/roomrates/dates/bulk`, {
        roomId: selRoom,
        dates: selectedDates,
        rate: parseFloat(rateInput),
      }, { headers: authHeaders });
      const ov = { ...overrides };
      for (const d of selectedDates) ov[`${selRoom}|${d}`] = parseFloat(rateInput);
      setOverrides(ov);
      setSelRoom(null); setSelStart(""); setSelEnd(""); setRateInput("");
      axios.get(`${API_BASE}/api/roomrates/rate-range`, { headers: authHeaders })
        .then(r => setRateRange(r.data || null)).catch(() => {});
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? "Failed to save rates");
    } finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!selRoom || selectedDates.length === 0) return;
    setSaving(true);
    setError("");
    try {
      await axios.delete(`${API_BASE}/api/roomrates/dates/bulk`, {
        data: { roomId: selRoom, dates: selectedDates },
        headers: authHeaders,
      });
      const ov = { ...overrides };
      for (const d of selectedDates) delete ov[`${selRoom}|${d}`];
      setOverrides(ov);
      setSelRoom(null); setSelStart(""); setSelEnd(""); setRateInput("");
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? "Failed to reset rates");
    } finally { setSaving(false); }
  };

  const todayISO = localISO(new Date());

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 60, color: "rgba(242,235,217,0.45)" }}>
        Loading rates…
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Controls row */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <button
          onClick={() => setYear(y => y - 1)}
          style={{ background: "rgba(14,30,56,0.8)", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 8, padding: "6px 14px", color: "rgba(242,235,217,0.65)", cursor: "pointer", fontSize: 13 }}
        >
          ← {year - 1}
        </button>
        <span style={{ fontWeight: 700, fontSize: 17, color: "#F2EBD9", minWidth: 50, textAlign: "center" }}>{year}</span>
        <button
          onClick={() => setYear(y => y + 1)}
          style={{ background: "rgba(14,30,56,0.8)", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 8, padding: "6px 14px", color: "rgba(242,235,217,0.65)", cursor: "pointer", fontSize: 13 }}
        >
          {year + 1} →
        </button>
        {pricingMarkets.length > 0 && (
          <div style={{
            background: "rgba(201,168,76,0.08)", border: "1px solid rgba(201,168,76,0.25)",
            borderRadius: 8, padding: "4px 6px 4px 12px", display: "flex", alignItems: "center", gap: 8,
          }}>
            <span style={{ fontSize: 10, color: "rgba(242,235,217,0.45)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Market</span>
            <select
              value={selectedMarket}
              onChange={e => setSelectedMarket(e.target.value)}
              style={{ background: "rgba(14,30,56,0.9)", border: "1px solid rgba(201,168,76,0.3)", borderRadius: 6, padding: "4px 8px", color: "#F2EBD9", fontSize: 12, fontWeight: 600, outline: "none", cursor: "pointer" }}
            >
              {pricingMarkets.map(m => (
                <option key={m.code} value={m.code}>{m.label} ({m.currency})</option>
              ))}
            </select>
          </div>
        )}
        <div style={{
          background: "rgba(201,168,76,0.08)", border: "1px solid rgba(201,168,76,0.25)",
          borderRadius: 8, padding: "4px 6px 4px 12px", display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ fontSize: 10, color: "rgba(242,235,217,0.45)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Occupancy</span>
          <select
            value={selectedOccupancy}
            onChange={e => setSelectedOccupancy(e.target.value as Occupancy)}
            style={{ background: "rgba(14,30,56,0.9)", border: "1px solid rgba(201,168,76,0.3)", borderRadius: 6, padding: "4px 8px", color: "#F2EBD9", fontSize: 12, fontWeight: 600, outline: "none", cursor: "pointer" }}
          >
            {OCCUPANCY_TIERS.map(o => (
              <option key={o} value={o}>{OCC_LABEL[o]}</option>
            ))}
          </select>
        </div>
        {rateRange && (
          <div style={{
            background: "rgba(201,168,76,0.08)", border: "1px solid rgba(201,168,76,0.25)",
            borderRadius: 8, padding: "5px 14px", display: "flex", alignItems: "center", gap: 6,
          }}>
            <span style={{ fontSize: 10, color: "rgba(242,235,217,0.45)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Rate Range</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#c9a84c" }}>
              {rateRange.min.toLocaleString()} – {rateRange.max.toLocaleString()} {currency}
            </span>
          </div>
        )}
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", gap: 14, fontSize: 11, alignItems: "center" }}>
          <span style={{ color: "rgba(242,235,217,0.45)" }}>■ Base</span>
          <span style={{ color: "#93c5fd" }}>■ Season rate</span>
          <span style={{ color: "#34d399" }}>■ Discounted</span>
          <span style={{ color: "#fbbf24" }}>■ Premium</span>
        </div>
      </div>

      {/* Bulk by Type panel */}
      {roomTypes.length > 0 && (
        <div style={{
          background: "#111E35", border: "1px solid rgba(201,168,76,0.18)",
          borderRadius: 12, padding: "14px 18px",
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(242,235,217,0.45)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 12 }}>
            Bulk Edit by Room Type
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <select
              value={bulkType}
              onChange={e => setBulkType(e.target.value)}
              style={{ ...FS, width: 160 }}
            >
              <option value="">Select type…</option>
              {roomTypes.map(t => (
                <option key={t} value={t}>{t} ({rooms.filter(r => r.roomType === t).length})</option>
              ))}
            </select>
            <input
              type="date"
              value={bulkFrom}
              onChange={e => setBulkFrom(e.target.value)}
              style={{ ...FS, width: 150 }}
            />
            <span style={{ color: "rgba(242,235,217,0.3)", fontSize: 13 }}>→</span>
            <input
              type="date"
              value={bulkTo}
              onChange={e => setBulkTo(e.target.value)}
              style={{ ...FS, width: 150 }}
            />
            <input
              type="number"
              min={0}
              placeholder={`Rate (${currency})`}
              value={bulkRate}
              onChange={e => setBulkRate(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleBulkApply()}
              style={{ ...FS, width: 140 }}
            />
            <button
              onClick={handleBulkApply}
              disabled={bulkSaving || !bulkType || !bulkFrom || !bulkTo || bulkRate === ""}
              style={{
                background: bulkSaving ? "rgba(201,168,76,0.25)" : "rgba(201,168,76,0.15)",
                border: "1px solid rgba(201,168,76,0.4)",
                borderRadius: 8, padding: "8px 18px", color: "#c9a84c", fontSize: 13, fontWeight: 700,
                cursor: (bulkSaving || !bulkType || !bulkFrom || !bulkTo || bulkRate === "") ? "default" : "pointer",
                opacity: (!bulkType || !bulkFrom || !bulkTo || bulkRate === "") ? 0.5 : 1,
                display: "flex", alignItems: "center", gap: 8, minWidth: 130, justifyContent: "center",
                transition: "background 0.2s",
              }}
            >
              {bulkSaving ? (
                <>
                  <span style={{
                    width: 13, height: 13, border: "2px solid rgba(201,168,76,0.3)",
                    borderTopColor: "#c9a84c", borderRadius: "50%",
                    display: "inline-block",
                    animation: "rp-spin 0.7s linear infinite",
                  }} />
                  Applying…
                </>
              ) : `Apply to ${bulkTypeRooms > 0 ? bulkTypeRooms + " rooms" : "all"}`}
            </button>
          </div>
          {bulkToast && (
            <div style={{
              marginTop: 10, fontSize: 12, fontWeight: 600,
              color: bulkToast.startsWith("Error") ? "#f87171" : "#34d399",
            }}>
              {bulkToast}
            </div>
          )}
        </div>
      )}

      {error && (
        <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 10, padding: "10px 14px", color: "#f87171", fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Calendar */}
      <div style={{
        background: "#0E1E38", border: "1px solid rgba(201,168,76,0.15)",
        borderRadius: 12, overflow: "hidden",
      }}>
        <div ref={calRef} style={{ overflowX: "auto" }}>
          <div style={{ minWidth: LABEL_W + days.length * DAY_W }}>

            {/* Month + day header */}
            <div style={{
              display: "flex", position: "sticky", top: 0, zIndex: 20,
              background: "#0a1628", borderBottom: "1px solid rgba(201,168,76,0.15)",
            }}>
              <div style={{
                width: LABEL_W, minWidth: LABEL_W, flexShrink: 0,
                padding: "8px 12px", display: "flex", alignItems: "center",
                borderRight: "1px solid rgba(201,168,76,0.15)",
                position: "sticky", left: 0, zIndex: 30,
                background: "#0a1628",
              }}>
                <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", color: "rgba(242,235,217,0.45)" }}>
                  Room / Rate
                </span>
              </div>
              <div style={{ display: "flex" }}>
                {monthGroups.map(g => (
                  <div key={g.month} style={{
                    width: g.count * DAY_W, flexShrink: 0,
                    borderRight: "1px solid rgba(201,168,76,0.15)",
                  }}>
                    <div style={{ padding: "4px 8px 0", fontSize: 10, fontWeight: 700, color: "#c9a84c", letterSpacing: "0.5px" }}>
                      {MONTHS[g.month]}
                    </div>
                    <div style={{ display: "flex" }}>
                      {days.slice(g.start, g.start + g.count).map(d => (
                        <div key={d.iso} style={{
                          width: DAY_W, flexShrink: 0, textAlign: "center",
                          fontSize: 10, padding: "2px 0 4px",
                          color: d.iso === todayISO ? "#c9a84c" : d.isWeekend ? "rgba(201,168,76,0.55)" : "rgba(242,235,217,0.5)",
                          fontWeight: d.iso === todayISO ? 800 : d.day === 1 ? 600 : 400,
                        }}>
                          {d.day}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Room rows */}
            {rooms.map((room, ri) => {
              const baseRate = baseRates[room.roomId] ?? 0;
              return (
                <div key={room.roomId} style={{
                  display: "flex",
                  borderBottom: ri < rooms.length - 1 ? "1px solid rgba(201,168,76,0.08)" : "none",
                  background: ri % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)",
                }}>
                  {/* Room label */}
                  <div style={{
                    width: LABEL_W, minWidth: LABEL_W, flexShrink: 0,
                    padding: "8px 12px", borderRight: "1px solid rgba(201,168,76,0.08)",
                    display: "flex", flexDirection: "column", gap: 2,
                    position: "sticky", left: 0, zIndex: 5,
                    background: ri % 2 === 0 ? "#0E1E38" : "#111d30",
                  }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#FDFAF4" }}>#{room.roomNumber}</span>
                    {room.roomType && <span style={{ fontSize: 10, color: "rgba(242,235,217,0.65)" }}>{room.roomType}</span>}
                    {baseRate > 0
                      ? <span style={{ fontSize: 10, color: "rgba(201,168,76,0.6)" }}>Base: {baseRate.toLocaleString()}</span>
                      : <span style={{ fontSize: 10, color: "rgba(242,235,217,0.3)", fontStyle: "italic" }}>No base rate</span>
                    }
                  </div>

                  {/* Day cells */}
                  <div style={{ display: "flex" }}>
                    {days.map(d => {
                      const key = `${room.roomId}|${d.iso}`;
                      const hasOverride = key in overrides;
                      const seasonRate = !hasOverride ? seasonRateFor(pricingSeasons, selectedMarket, room.roomType, d.iso, selectedOccupancy) : null;
                      const source: "manual" | "pricing" | "base" = hasOverride ? "manual" : seasonRate != null ? "pricing" : "base";
                      const rate = hasOverride ? overrides[key] : (seasonRate ?? baseRate);
                      const isSel = selRoom === room.roomId && selMin && selMax && d.iso >= selMin && d.iso <= selMax;
                      const isToday = d.iso === todayISO;
                      const st = rateStyle(rate, baseRate, source);

                      return (
                        <div
                          key={d.iso}
                          style={{
                            width: DAY_W, height: 44, flexShrink: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 11, fontWeight: isSel ? 700 : st.weight,
                            color: isSel ? "#FDFAF4" : st.color,
                            background: isSel
                              ? "rgba(201,168,76,0.28)"
                              : isToday ? "rgba(201,168,76,0.06)" : st.bg,
                            borderRight: d.isMonthEnd ? "1px solid rgba(201,168,76,0.18)" : "none",
                            borderLeft: isToday ? "2px solid rgba(201,168,76,0.6)" : "none",
                            cursor: "pointer",
                            userSelect: "none",
                            transition: "background 0.08s",
                          }}
                          onMouseDown={() => {
                            isDragging.current = true;
                            setSelRoom(room.roomId);
                            setSelStart(d.iso);
                            setSelEnd(d.iso);
                            setRateInput(hasOverride ? String(Math.round(overrides[key])) : "");
                          }}
                          onMouseEnter={() => {
                            if (isDragging.current && selRoom === room.roomId) {
                              setSelEnd(d.iso);
                            }
                          }}
                          onMouseUp={() => { isDragging.current = false; }}
                        >
                          {formatRate(rate)}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {rooms.length === 0 && (
              <div style={{ textAlign: "center", color: "rgba(242,235,217,0.45)", padding: 40 }}>
                No rooms found. Add rooms first to manage rates.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Selection action bar */}
      {selRoom && selMin && (
        <div style={{
          background: "#0a1628", border: "1px solid rgba(201,168,76,0.25)",
          borderRadius: 12, padding: "14px 20px", display: "flex", alignItems: "center", gap: 16,
          boxShadow: "0 4px 24px rgba(0,0,0,0.5)",
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: "rgba(242,235,217,0.3)" }}>
              <strong style={{ color: "#F2EBD9" }}>
                Room #{rooms.find(r => r.roomId === selRoom)?.roomNumber}
              </strong>
              {selMin === selMax
                ? <span style={{ marginLeft: 8 }}>{selMin}</span>
                : <span style={{ marginLeft: 8 }}>{selMin} → {selMax}</span>
              }
              <span style={{ color: "#C9A84C", marginLeft: 10 }}>
                {selectedDates.length} night{selectedDates.length !== 1 ? "s" : ""}
              </span>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="number"
              min={0}
              placeholder={`Rate (${currency})`}
              value={rateInput}
              onChange={e => setRateInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSetRate()}
              style={{ ...FS, width: 140 }}
              autoFocus
            />
            <button
              onClick={handleSetRate}
              disabled={saving || !rateInput}
              style={{
                background: saving ? "rgba(201,168,76,0.25)" : "rgba(201,168,76,0.18)",
                border: "1px solid rgba(201,168,76,0.4)", borderRadius: 8,
                padding: "8px 18px", color: "#c9a84c", fontSize: 13, fontWeight: 700,
                cursor: saving || !rateInput ? "default" : "pointer",
                opacity: !rateInput ? 0.5 : 1,
                display: "flex", alignItems: "center", gap: 8, minWidth: 100, justifyContent: "center",
              }}
            >
              {saving ? (
                <>
                  <span style={{
                    width: 12, height: 12, border: "2px solid rgba(201,168,76,0.3)",
                    borderTopColor: "#c9a84c", borderRadius: "50%",
                    display: "inline-block",
                    animation: "rp-spin 0.7s linear infinite",
                  }} />
                  Saving…
                </>
              ) : "Set Rate"}
            </button>
            <button
              onClick={handleReset}
              disabled={saving}
              style={{
                background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)",
                borderRadius: 8, padding: "8px 14px", color: "#f87171", fontSize: 13, cursor: "pointer",
              }}
            >
              Reset to Base
            </button>
            <button
              onClick={() => { setSelRoom(null); setSelStart(""); setSelEnd(""); setRateInput(""); }}
              style={{
                background: "transparent", border: "1px solid rgba(242,235,217,0.15)",
                borderRadius: 8, padding: "8px 12px", color: "rgba(242,235,217,0.55)", fontSize: 13, cursor: "pointer",
              }}
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
